/**
 * A link into the API documentation.
 *
 * Always a new tab. The documentation is read *alongside* the dashboard -- most often beside the
 * playground, whose pasted API key and request options live in component state only and would be
 * gone on return from a same-tab navigation. `noopener` keeps the site from reaching back into
 * this window through `window.opener`.
 *
 * The new tab is announced to screen readers, since nothing visual about a link says it will
 * leave the page.
 */
import type { ReactNode } from 'react';
import { docsUrl, type DocsSection } from '../lib/docs-links';

const INLINE = 'link font-medium';

export function DocsLink({
  section,
  className,
  children,
}: {
  section?: DocsSection;
  /** Replaces the inline-text styling entirely, for links that are not in running text. */
  className?: string;
  children: ReactNode;
}) {
  return (
    <a
      href={docsUrl(section)}
      target="_blank"
      rel="noopener noreferrer"
      className={className ?? INLINE}
    >
      {children}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}
